import ReactGA from "react-ga4";

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;
const CONSENT_KEY = "analyticsConsent";

let gaInitialized = false;
let clarityInitialized = false;

const hasAnalyticsConsent = () => {
  if (process.env.REACT_APP_ANALYTICS_REQUIRE_CONSENT !== "true") {
    return true;
  }
  return localStorage.getItem(CONSENT_KEY) === "true";
};

const loadClarity = () => {
  if (!CLARITY_ID || clarityInitialized || typeof window === "undefined") return;
  clarityInitialized = true;

  window.clarity =
    window.clarity ||
    function clarityStub(...args) {
      (window.clarity.q = window.clarity.q || []).push(args);
    };

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
  document.head.appendChild(script);
};

export const initAnalytics = () => {
  if (!hasAnalyticsConsent()) return;

  if (GA4_ID && !gaInitialized) {
    ReactGA.initialize(GA4_ID, {
      gaOptions: { send_page_view: false },
    });
    gaInitialized = true;
  }

  loadClarity();
};

export const grantAnalyticsConsent = () => {
  localStorage.setItem(CONSENT_KEY, "true");
  initAnalytics();
  trackPageView(window.location.pathname + window.location.search, document.title);
};

export const denyAnalyticsConsent = () => {
  localStorage.setItem(CONSENT_KEY, "false");
};

export const trackPageView = (path, title) => {
  if (!GA4_ID || !hasAnalyticsConsent()) return;

  initAnalytics();

  ReactGA.send({
    hitType: "pageview",
    page: path,
    title: title || document.title,
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (!GA4_ID || !hasAnalyticsConsent()) return;

  initAnalytics();

  ReactGA.event(eventName, {
    page_path: window.location.pathname,
    ...params,
  });
};

export const setupClickTracking = () => {
  const handler = (event) => {
    const target = event.target.closest("[data-track]");
    if (!target) return;

    trackEvent(target.dataset.track, {
      location: target.dataset.trackLocation || undefined,
      label: target.dataset.trackLabel || undefined,
      item_name: target.dataset.trackItem || undefined,
      link_url: target.getAttribute("href") || undefined,
      spots_remaining: target.dataset.trackSpotsRemaining || undefined,
      urgency_level: target.dataset.trackUrgencyLevel || undefined,
      ...(target.dataset.trackFilterActive === "true"
        ? {
            filter_active: "true",
            filter_days: target.dataset.trackFilterDays || undefined,
            filter_time: target.dataset.trackFilterTime || undefined,
            filter_age_years: target.dataset.trackFilterAgeYears || undefined,
            filter_age_months: target.dataset.trackFilterAgeMonths || undefined,
            filter_open_spots_only:
              target.dataset.trackFilterOpenSpotsOnly || undefined,
          }
        : {}),
    });
  };

  document.addEventListener("click", handler, true);
  return () => document.removeEventListener("click", handler, true);
};

/** GA4 event names used across the site */
export const AnalyticsEvents = {
  REGISTER: "register_click",
  PARENT_PORTAL: "parent_portal_click",
  MEET_REGISTER: "meet_register_click",
  MEET_TOURISM: "meet_tourism_click",
  MEET_SECTION_NAV: "meet_section_nav_click",
  MEET_SECTION_VIEW: "meet_section_view",
  MEET_FAQ: "meet_faq_toggle",
  MEET_CONTACT: "meet_contact_click",
  MEET_VENUE: "meet_venue_click",
  MEET_SPONSOR: "meet_sponsor_click",
  MEET_SOCIAL: "meet_social_click",
  MEET_STICKY_VIEW: "meet_sticky_cta_view",
  EVENT_REGISTER: "event_register_click",
  EVENT_VIEW: "event_view",
  EVENT_DETAILS: "event_details_toggle",
  PHONE: "phone_click",
  EMAIL: "email_click",
  STAFF_BIO: "staff_bio_toggle",
  PROGRAM_CLASS: "program_class_click",
  PROGRAM: "program_click",
  CALENDAR: "calendar_click",
  SOCIAL: "social_click",
  JOB_APPLY: "job_apply_click",
  EMPLOYMENT: "employment_apply_click",
  EVENTS_NAV: "events_nav_click",
  STAFF_NAV: "staff_nav_click",
  EXPLORE_PROGRAMS: "explore_programs_click",
  SITE_ANNOUNCEMENT_CLICK: "site_announcement_click",
  SITE_ANNOUNCEMENT_DISMISS: "site_announcement_dismiss",
  SCHEDULE_FILTER_VIEW: "schedule_filter_view",
  SCHEDULE_FILTER_DAY: "schedule_filter_day",
  SCHEDULE_FILTER_TIME: "schedule_filter_time",
  SCHEDULE_FILTER_AGE: "schedule_filter_age",
  SCHEDULE_FILTER_OPEN_SPOTS: "schedule_filter_open_spots",
  SCHEDULE_FILTER_CLEAR: "schedule_filter_clear",
  SCHEDULE_FILTER_RESULTS: "schedule_filter_results",
  SCHEDULE_FILTER_EMPTY_VIEW: "schedule_filter_empty_view",
  SCHEDULE_FILTER_FIRST_USE: "schedule_filter_first_use",
  HOME_CLASS_FINDER_SUBMIT: "home_class_finder_submit",
};
